import { useMemo } from 'react';

import { useGetEnabledModuleTabs } from '~/data/moduleTabs';
import {
  EmployeeModuleConfig,
  EmployeeModuleTabConfig,
} from '~/scenes/PortalViews/Views/EmployeeViews/Modules/types';

import {
  filterTabConfigsByEnabledConfigKeys,
  getDefaultTabPath,
  getPathsFromTabConfigs,
} from './data';

type UseEnabledModuleTabPathsOutput<TabPath, TabConfigKey, TabInstructionKey> =
  {
    isLoading: boolean;
    enabledTabConfigs: EmployeeModuleTabConfig<
      TabPath,
      TabConfigKey,
      TabInstructionKey
    >[];
    enabledTabPaths: TabPath[];
    defaultEnabledTabPath: TabPath | undefined;
    defaultTab: TabPath;
  };

type PropsType<TabPath, TabConfigKey, TabInstructionKey> = {
  availableTabs?: TabPath[];
} & EmployeeModuleConfig<TabPath, TabConfigKey, TabInstructionKey>;

export const useEnabledModuleTabPaths = <
  TabPath,
  TabConfigKey,
  TabInstructionKey,
>(
  props: PropsType<TabPath, TabConfigKey, TabInstructionKey>,
): UseEnabledModuleTabPathsOutput<TabPath, TabConfigKey, TabInstructionKey> => {
  const { tabConfigs, enabledTabsRequestKey, defaultTab, availableTabs } =
    props;

  const { isLoading: isLoadingEnabledTabs, data: enabledTabsKeys } =
    useGetEnabledModuleTabs({
      moduleTabsRequestKey: enabledTabsRequestKey,
    });

  const { enabledTabConfigs, enabledTabPaths, defaultEnabledTabPath } =
    useMemo(() => {
      if (isLoadingEnabledTabs || typeof enabledTabsKeys === 'undefined') {
        return {
          enabledTabConfigs: [],
          enabledTabPaths: [],
          defaultEnabledTabPath: undefined,
        };
      }

      const availableTabConfigs =
        !!availableTabs && availableTabs.length > 0
          ? tabConfigs.filter((c) => availableTabs.includes(c.path))
          : tabConfigs;

      const enabledTabConfigs = filterTabConfigsByEnabledConfigKeys(
        availableTabConfigs,
        enabledTabsKeys,
      );

      const enabledTabPaths = getPathsFromTabConfigs(enabledTabConfigs);

      const defaultEnabledTabPath = getDefaultTabPath(
        enabledTabPaths,
        defaultTab,
      );

      return {
        enabledTabConfigs,
        enabledTabPaths,
        defaultEnabledTabPath,
      };
    }, [
      isLoadingEnabledTabs,
      enabledTabsKeys,
      tabConfigs,
      defaultTab,
      availableTabs,
    ]);

  return {
    isLoading: isLoadingEnabledTabs,
    enabledTabConfigs,
    enabledTabPaths,
    defaultEnabledTabPath,
    defaultTab: defaultEnabledTabPath ?? defaultTab,
  };
};
